import React, { memo, useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
    Undo2, Plus, PencilLine, Trash2, ArrowRight, Loader2,
    AlertTriangle, Sparkles, RotateCcw, User, CheckCircle2,
} from 'lucide-react';
import { InlineEditable } from '../tasks/InlineEditable';
import { FieldPicker, TaskAttributeRow } from '../tasks/TaskFieldChips';
import { DueBadge } from '../tasks/DueBadge';
import { STATUS_META, FIELD_LABELS, formatFieldValue } from '../tasks/taskMeta';
import { cn } from '../../lib/utils';

/**
 * The agent's changeset: what it did to your tasks, this turn and every turn
 * before it in this conversation.
 *
 * Everything here is live — the rows are not a read-only receipt. A created or
 * updated task can be renamed, re-scoped and re-prioritised in place, and any
 * single change (or a whole turn) can be handed back with one click.
 */

const OP_META = {
    create: { label: 'Created', icon: Plus, rail: 'bg-emerald-500', text: 'text-emerald-400', tint: 'bg-emerald-500/[0.04]' },
    update: { label: 'Updated', icon: PencilLine, rail: 'bg-primary', text: 'text-primary', tint: 'bg-primary/[0.04]' },
    delete: { label: 'Deleted', icon: Trash2, rail: 'bg-red-500', text: 'text-red-400', tint: 'bg-red-500/[0.04]' },
};

const SPRING = { type: 'spring', stiffness: 260, damping: 24 };

function formatTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ═══════════════════════════════════════════════════════════════════════════════
// FIELD DIFF
// ═══════════════════════════════════════════════════════════════════════════════

const FieldDiff = memo(function FieldDiff({ field, before, after, drifted }) {
    return (
        <div className="flex items-baseline gap-2 font-mono text-[11px] leading-relaxed">
            <span className="w-[4.5rem] flex-shrink-0 truncate uppercase tracking-wide text-muted-foreground/60">
                {FIELD_LABELS[field] ?? field}
            </span>
            <span className="truncate text-muted-foreground/70 line-through decoration-muted-foreground/40">
                {formatFieldValue(field, before)}
            </span>
            <ArrowRight className="h-3 w-3 flex-shrink-0 text-muted-foreground/40" />
            <span className={cn('min-w-0 truncate font-medium', drifted ? 'text-muted-foreground/60 line-through' : 'text-accent')}>
                {formatFieldValue(field, after)}
            </span>
        </div>
    );
});

// ═══════════════════════════════════════════════════════════════════════════════
// CHANGE ROW
// ═══════════════════════════════════════════════════════════════════════════════

const TaskChangeRow = memo(function TaskChangeRow({
    change,
    task,
    index,
    readOnly,
    isReverting,
    reduceMotion,
    onRevert,
    onUpdateTask,
    onDeleteTask,
}) {
    const [confirmingForce, setConfirmingForce] = useState(false);
    const op = OP_META[change.op] ?? OP_META.update;
    const OpIcon = op.icon;

    const isReverted = change.reverted;
    const conflict = change.conflict;
    const driftedFields = useMemo(() => new Set(conflict?.fields ?? []), [conflict]);
    // A deleted task is gone from the board, so we render from the snapshot.
    const snapshot = task ?? change.before ?? change.after;
    const isLive = !!task && !isReverted;

    // The live value first: the badge must agree with the board, and this row
    // stays mounted while you edit the task inside it. `after` before `before`
    // so a reschedule shows where it landed, not where it came from — the diff
    // line right below already says that.
    const dueDate = task?.due_date ?? change.after?.due_date ?? change.before?.due_date ?? null;

    const handleRevert = useCallback(() => {
        if (conflict && !confirmingForce) {
            setConfirmingForce(true);
            return;
        }
        setConfirmingForce(false);
        onRevert(change, { force: !!conflict });
    }, [change, conflict, confirmingForce, onRevert]);

    const patch = useCallback((updates) => onUpdateTask(task.id, updates), [onUpdateTask, task]);

    const revertLabel = isReverted
        ? 'Reverted'
        : confirmingForce
            ? 'Revert anyway?'
            : change.op === 'delete'
                ? 'Restore this task'
                : change.op === 'create'
                    ? 'Undo — deletes this task'
                    : 'Undo this change';

    return (
        <motion.div
            layout={!reduceMotion}
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 12, height: 0, marginTop: 0 }}
            transition={{ ...SPRING, delay: reduceMotion ? 0 : Math.min(index * 0.035, 0.28) }}
            data-testid={`task-change-${change.id}`}
            className={cn(
                'group/row relative flex gap-3 border-b border-white/5 py-2.5 pl-3 pr-2 transition-colors',
                isReverted ? 'opacity-45' : op.tint,
                !isReverted && 'hover:bg-white/[0.03]',
            )}
        >
            {/* Op rail */}
            <span
                aria-hidden
                className={cn(
                    'absolute left-0 top-0 h-full w-[2px]',
                    isReverted ? 'bg-white/10' : op.rail,
                )}
            />

            <div className="min-w-0 flex-1">
                {/* Op label line */}
                <div className="flex items-center gap-1.5">
                    <OpIcon className={cn('h-3 w-3 flex-shrink-0', isReverted ? 'text-muted-foreground' : op.text)} />
                    <span
                        className={cn(
                            'font-mono text-[10px] font-bold uppercase tracking-[0.16em]',
                            isReverted ? 'text-muted-foreground' : op.text,
                        )}
                    >
                        {op.label}
                    </span>
                    {change.source === 'user' ? (
                        <span className="flex items-center gap-1 rounded border border-white/10 bg-white/5 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                            <User className="h-2.5 w-2.5" /> you
                        </span>
                    ) : null}
                    {isReverted ? (
                        <span className="rounded border border-white/10 bg-white/5 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-muted-foreground">
                            reverted
                        </span>
                    ) : null}
                    {!isReverted && conflict?.reason === 'modified_since' ? (
                        <span
                            title={`You changed ${conflict.fields.join(', ')} after the agent did`}
                            className="flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/15 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-amber-400"
                        >
                            <AlertTriangle className="h-2.5 w-2.5" /> edited by you
                        </span>
                    ) : null}
                </div>

                {/* Title */}
                <div className="mt-0.5 flex items-start gap-1.5">
                    {isLive ? (
                        <button
                            type="button"
                            title={`Mark as ${task.status === 'done' ? 'to do' : 'done'}`}
                            data-testid={`task-toggle-${task.id}`}
                            disabled={readOnly}
                            onClick={() => patch({ status: task.status === 'done' ? 'todo' : 'done' })}
                            className="mt-1.5 flex-shrink-0 disabled:cursor-default"
                        >
                            {task.status === 'done' ? (
                                <STATUS_META.done.icon className="h-4 w-4 text-emerald-500" />
                            ) : (
                                <span className="block h-4 w-4 rounded-full border-2 border-muted-foreground transition-colors hover:border-emerald-500 hover:bg-emerald-500/20" />
                            )}
                        </button>
                    ) : null}

                    <div className="min-w-0 flex-1">
                        {isLive ? (
                            <InlineEditable
                                value={task.title}
                                disabled={readOnly}
                                onCommit={(title) => title && patch({ title })}
                                placeholder="Untitled task"
                                testId={`task-title-${task.id}`}
                                ariaLabel="Task title"
                                className={cn(
                                    '-ml-2 text-sm font-medium',
                                    task.status === 'done' && 'text-muted-foreground line-through',
                                )}
                            />
                        ) : (
                            <p
                                className={cn(
                                    'px-0 py-1 text-sm font-medium',
                                    (change.op === 'delete' || isReverted) && 'text-muted-foreground line-through',
                                )}
                            >
                                {change.task_title || 'Untitled task'}
                            </p>
                        )}
                    </div>

                    {/* When the task is gone the date comes off the snapshot, so
                        a deleted task still says what it was due — and clearing
                        is offered only where there is something to clear on. */}
                    <DueBadge
                        due={dueDate}
                        size="sm"
                        className="mt-1"
                        testId={`task-due-${change.task_id}`}
                        onClear={isLive && !readOnly ? () => patch({ due_date: null }) : undefined}
                    />
                </div>

                {/* Body: field diffs for updates, attribute chips for creates */}
                <div className="mt-1 space-y-1 pl-0.5">
                    {change.op === 'update'
                        ? change.fields.map((field) => (
                            <FieldDiff
                                key={field}
                                field={field}
                                before={change.before?.[field]}
                                after={change.after?.[field]}
                                drifted={driftedFields.has(field)}
                            />
                        ))
                        : null}

                    {isLive ? (
                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                            <FieldPicker
                                field="status"
                                value={task.status}
                                disabled={readOnly}
                                onChange={(status) => patch({ status })}
                                testId={`task-${task.id}-status`}
                            />
                            <TaskAttributeRow task={task} onUpdate={readOnly ? undefined : patch} disabled={readOnly} />
                        </div>
                    ) : null}

                    {change.op === 'create' && !isLive && snapshot ? (
                        <p className="font-mono text-[11px] text-muted-foreground/60">
                            This task no longer exists.
                        </p>
                    ) : null}

                    {isLive ? (
                        <InlineEditable
                            value={task.description}
                            disabled={readOnly}
                            multiline
                            onCommit={(description) => patch({ description })}
                            placeholder="Add notes…"
                            testId={`task-notes-${task.id}`}
                            ariaLabel="Task notes"
                            className="-ml-2 mt-0.5 text-xs text-muted-foreground/80"
                        />
                    ) : null}
                </div>
            </div>

            {/* Actions */}
            {!readOnly ? (
                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                    <button
                        type="button"
                        title={revertLabel}
                        aria-label={revertLabel}
                        data-testid={`task-change-revert-${change.id}`}
                        disabled={isReverted || isReverting}
                        onClick={handleRevert}
                        onBlur={() => setConfirmingForce(false)}
                        className={cn(
                            'flex h-8 min-w-8 items-center gap-1 rounded-lg px-2 transition-colors',
                            'md:opacity-0 md:group-hover/row:opacity-100 md:focus-visible:opacity-100',
                            confirmingForce
                                ? 'bg-amber-500/20 text-amber-400 opacity-100 md:opacity-100'
                                : 'text-muted-foreground hover:bg-white/10 hover:text-foreground',
                            (isReverted || isReverting) && 'cursor-default opacity-30 md:opacity-30',
                        )}
                    >
                        {isReverting ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : change.op === 'delete' ? (
                            <RotateCcw className="h-3.5 w-3.5" />
                        ) : (
                            <Undo2 className="h-3.5 w-3.5" />
                        )}
                        {confirmingForce ? <span className="text-[10px] font-bold uppercase">Sure?</span> : null}
                    </button>

                    {isLive ? (
                        <button
                            type="button"
                            title="Delete task"
                            aria-label="Delete task"
                            data-testid={`task-delete-${task.id}`}
                            onClick={() => onDeleteTask(task.id)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-red-500/20 hover:text-red-400 md:opacity-0 md:group-hover/row:opacity-100 md:focus-visible:opacity-100"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                </div>
            ) : null}
        </motion.div>
    );
});

// ═══════════════════════════════════════════════════════════════════════════════
// OUTCOME ROW — what the turn decided not to write
//
// A change log only records writes, so the two outcomes that write nothing used
// to be rendered as nothing: ask for something already true and the panel showed
// an empty turn; reference a task that has since been deleted and it showed the
// same. "Nothing to do" and "it didn't work" looked identical, and only one of
// them was true. These rows are deliberately quiet — they are not undoable and
// there is nothing to act on — but they are present.
// ═══════════════════════════════════════════════════════════════════════════════

const SKIP_REASONS = {
    not_found: 'no longer exists',
    invalid_id: 'unrecognised id',
    missing_title: 'no title given',
};

const OutcomeRow = memo(function OutcomeRow({ kind, title, detail, index, reduceMotion }) {
    const isSkip = kind === 'skipped';
    const Icon = isSkip ? AlertTriangle : CheckCircle2;

    return (
        <motion.div
            layout={!reduceMotion}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ ...SPRING, delay: reduceMotion ? 0 : Math.min(index * 0.035, 0.28) }}
            data-testid={`task-outcome-${kind}`}
            className="relative flex items-start gap-2.5 border-b border-white/5 py-2.5 pl-3 pr-2"
        >
            <span aria-hidden className={cn('absolute left-0 top-0 h-full w-[2px]', isSkip ? 'bg-amber-500/60' : 'bg-white/10')} />
            <Icon className={cn('mt-0.5 h-3.5 w-3.5 flex-shrink-0', isSkip ? 'text-amber-400' : 'text-muted-foreground/60')} />
            <div className="min-w-0 flex-1">
                <p className={cn('truncate text-sm', isSkip ? 'text-muted-foreground' : 'text-muted-foreground/80')}>
                    {title || 'Untitled task'}
                </p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground/50">
                    {detail}
                </p>
            </div>
        </motion.div>
    );
});

// ═══════════════════════════════════════════════════════════════════════════════
// CHANGESET GROUP (one agent turn)
// ═══════════════════════════════════════════════════════════════════════════════

const ChangesetGroup = memo(function ChangesetGroup({
    batch,
    tasksById,
    readOnly,
    isLatest,
    revertingIds,
    reduceMotion,
    onRevertChange,
    onRevertBatch,
    onUpdateTask,
    onDeleteTask,
}) {
    const [confirmingBatch, setConfirmingBatch] = useState(false);
    const canRevert = batch.active > 0 && !readOnly;
    const batchReverting = revertingIds.has(batch.batch_id);
    const noop = batch.noop ?? [];
    const skipped = batch.skipped ?? [];

    const handleBatchRevert = useCallback(() => {
        if (batch.conflicted && !confirmingBatch) {
            setConfirmingBatch(true);
            return;
        }
        setConfirmingBatch(false);
        onRevertBatch(batch, { force: !!batch.conflicted });
    }, [batch, confirmingBatch, onRevertBatch]);

    return (
        <section data-testid={`changeset-${batch.batch_id}`} className="scroll-mt-4">
            <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-white/10 bg-[#0b0b0e]/95 px-3 py-2 backdrop-blur-xl">
                <div className="flex min-w-0 items-center gap-2">
                    <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                        {isLatest ? 'This turn' : 'Earlier'}
                    </span>
                    <span className="text-[10px] text-muted-foreground/50">·</span>
                    <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70">
                        {formatTime(batch.created_at)}
                    </span>
                    <span className="text-[10px] text-muted-foreground/50">·</span>
                    <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70">
                        {batch.total > 0
                            ? `${batch.active}/${batch.total}`
                            : 'no changes'}
                    </span>
                </div>

                {canRevert ? (
                    <button
                        type="button"
                        data-testid={`changeset-revert-${batch.batch_id}`}
                        onClick={handleBatchRevert}
                        onBlur={() => setConfirmingBatch(false)}
                        disabled={batchReverting}
                        className={cn(
                            'flex flex-shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors',
                            confirmingBatch
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'text-muted-foreground hover:bg-white/10 hover:text-foreground',
                        )}
                    >
                        {batchReverting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                        {confirmingBatch ? 'Revert anyway?' : 'Revert all'}
                    </button>
                ) : null}
            </header>

            <AnimatePresence initial={false}>
                {batch.changes.map((change, i) => (
                    <TaskChangeRow
                        key={change.id}
                        change={change}
                        task={tasksById.get(change.task_id)}
                        index={i}
                        readOnly={readOnly}
                        isReverting={revertingIds.has(change.id) || batchReverting}
                        reduceMotion={reduceMotion}
                        onRevert={onRevertChange}
                        onUpdateTask={onUpdateTask}
                        onDeleteTask={onDeleteTask}
                    />
                ))}

                {noop.map((item, i) => (
                    <OutcomeRow
                        key={`noop-${item.task_id ?? i}`}
                        kind="noop"
                        title={item.title}
                        detail={
                            item.fields_requested?.length
                                ? `already ${item.fields_requested.map(f => FIELD_LABELS[f] ?? f).join(', ')}`
                                : 'already in that state'
                        }
                        index={batch.changes.length + i}
                        reduceMotion={reduceMotion}
                    />
                ))}

                {skipped.map((item, i) => (
                    <OutcomeRow
                        key={`skipped-${item.task_id ?? i}`}
                        kind="skipped"
                        title={item.title || item.task_title}
                        detail={`skipped — ${SKIP_REASONS[item.reason] ?? item.reason ?? 'could not be matched'}`}
                        index={batch.changes.length + noop.length + i}
                        reduceMotion={reduceMotion}
                    />
                ))}
            </AnimatePresence>
        </section>
    );
});

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════

function TaskChangesetImpl({
    batches = [],
    tasks = [],
    readOnly = false,
    revertingIds,
    onRevertChange,
    onRevertBatch,
    onUpdateTask,
    onDeleteTask,
}) {
    const reduceMotion = useReducedMotion();

    // One pass instead of a .find() per row — changesets get long in a busy session.
    const tasksById = useMemo(() => {
        const map = new Map();
        for (const task of tasks) map.set(task.id, task);
        return map;
    }, [tasks]);

    // No "applying" state any more: the writes land inside the chat turn, so a
    // changeset is already settled by the time the reply that carries it does.
    if (batches.length === 0) {
        return (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
                    <Sparkles className="h-5 w-5 text-muted-foreground/60" />
                </div>
                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    No agent activity yet
                    <span className="forge-caret ml-0.5 text-primary">_</span>
                </p>
                <p className="mt-3 max-w-[26ch] text-xs leading-relaxed text-muted-foreground/60">
                    Ask the agent to plan, re-prioritise or close out work and every change lands here — fully editable and undoable.
                </p>
            </div>
        );
    }

    return (
        <div>
            {batches.map((batch, i) => (
                <ChangesetGroup
                    key={batch.batch_id}
                    batch={batch}
                    tasksById={tasksById}
                    readOnly={readOnly}
                    isLatest={i === 0}
                    revertingIds={revertingIds}
                    reduceMotion={reduceMotion}
                    onRevertChange={onRevertChange}
                    onRevertBatch={onRevertBatch}
                    onUpdateTask={onUpdateTask}
                    onDeleteTask={onDeleteTask}
                />
            ))}
        </div>
    );
}

export const TaskChangeset = memo(TaskChangesetImpl);
export default TaskChangeset;
